import React from 'react'
import {Heart, Target, Users, BookOpen} from 'lucide-react'
import { motion } from 'framer-motion'

const About: React.FC = () => {
  const values = [
    {
      icon: Heart,
      title: 'Amor e Acolhimento',
      description: 'Recebemos cada pessoa com amor genuíno, criando um ambiente de refúgio e paz.'
    },
    {
      icon: Target,
      title: 'Propósito Divino',
      description: 'Guiamos vidas ao encontro com Cristo e ao cumprimento do propósito de Deus.'
    },
    {
      icon: Users,
      title: 'Comunhão',
      description: 'Fortalecemos laços fraternos, edificando uns aos outros na fé.'
    },
    {
      icon: BookOpen,
      title: 'Palavra de Deus',
      description: 'Fundamentamos tudo no ensino bíblico, vivendo segundo a verdade das Escrituras.'
    }
  ]

  return (
    <div className="min-h-screen">
      {/* Header */}
      <section className="bg-gradient-to-br from-[#1e3a8a] to-[#6b8f71] py-20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <h1 className="text-5xl font-bold text-white mb-6">
              Sobre Nós
            </h1>
            <p className="text-xl text-white/90">
              Conheça nossa história, missão e valores
            </p>
          </motion.div>
        </div>
      </section>

      {/* Nossa História */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-4">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true }}
            className="prose prose-lg max-w-none"
          >
            <h2 className="text-4xl font-bold text-[#1e3a8a] mb-8 text-center">
              Nossa História
            </h2>
            <div className="text-gray-700 leading-relaxed space-y-4">
              <p>
                O <strong>REFUGE IN CHRIST MINISTRY</strong> nasceu do desejo de criar um espaço onde pessoas possam 
                encontrar acolhimento, proteção espiritual e uma conexão genuína com Deus. Como portas abertas, 
                recebemos a todos que buscam refúgio em meio às tempestades da vida.
              </p>
              <p>
                Nossa jornada começou com um pequeno grupo de irmãos que compartilhavam a visão de edificar uma 
                comunidade fundamentada no amor de Cristo e no poder transformador da Palavra de Deus. Ao longo 
                dos anos, crescemos não apenas em número, mas em profundidade espiritual e compromisso com o Reino.
              </p>
              <p>
                Hoje, somos uma família diversa, unida pela fé em Jesus Cristo, dedicada a viver e compartilhar 
                o evangelho através da adoração, ensino, comunhão e serviço.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Missão e Visão */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid md:grid-cols-2 gap-12">
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="bg-[#1e3a8a] w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Target className="text-white" size={32} />
              </div>
              <h3 className="text-3xl font-bold text-[#1e3a8a] mb-4">
                Nossa Missão
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Ser um refúgio de fé, esperança e amor, onde vidas são transformadas pelo poder de Cristo. 
                Proclamamos o evangelho com clareza, edificamos discípulos comprometidos e servimos à 
                comunidade com excelência e compaixão.
              </p>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="bg-white p-8 rounded-xl shadow-lg"
            >
              <div className="bg-[#6b8f71] w-16 h-16 rounded-lg flex items-center justify-center mb-6">
                <Heart className="text-white" size={32} />
              </div>
              <h3 className="text-3xl font-bold text-[#1e3a8a] mb-4">
                Nossa Visão
              </h3>
              <p className="text-gray-700 leading-relaxed">
                Ser reconhecidos como uma comunidade vibrante e acolhedora, onde cada pessoa encontra seu 
                lugar no corpo de Cristo, cresce na maturidade espiritual e impacta o mundo com o amor 
                transformador do evangelho.
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Valores */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-4xl font-bold text-[#1e3a8a] mb-12 text-center">
            Nossos Valores
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            {values.map((value, index) => (
              <motion.div
                key={value.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
                className="flex gap-4"
              >
                <div className="flex-shrink-0">
                  <div className="bg-[#6b8f71] w-12 h-12 rounded-lg flex items-center justify-center">
                    <value.icon className="text-white" size={24} />
                  </div>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-[#1e3a8a] mb-2">
                    {value.title}
                  </h3>
                  <p className="text-gray-700">
                    {value.description}
                  </p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#1e3a8a] to-[#6b8f71]">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Faça Parte Desta Família
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Você é sempre bem-vindo em nosso meio. Venha nos visitar!
          </p>
        </div>
      </section>
    </div>
  )
}

export default About
